import { skipToken, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { keys } from './keys';
import { invalidate, type QueryOptions } from './query-client';
import { request } from './client';
import type {
  CredentialsCheck,
  DomainCheck,
  EmailDomain,
  MailboxResult,
  WhitelistResult,
} from './types';

/**
 * `/tenants/:tenantId/email`: what a domain still needs, plus the two
 * additive writes MXroute allows.
 *
 * The hub does not write DNS and does not add MXroute domains — `check`
 * reports the to-do list and a human does those steps. `domain` is optional
 * and only picks among the tenant's own domains.
 */
export const emailApi = {
  domains: (tenantId: string) => request<EmailDomain[]>(`/tenants/${tenantId}/email/domains`),
  check: (tenantId: string, domain?: string) =>
    request<DomainCheck>(`/tenants/${tenantId}/email/check`, {
      query: domain ? { domain } : {},
    }),
  createMailbox: (
    tenantId: string,
    body: { localPart: string; password: string; quotaMb?: number; domain?: string },
  ) => request<MailboxResult>(`/tenants/${tenantId}/email/mailboxes`, { method: 'POST', body }),
  addWhitelist: (tenantId: string, body: { entries: string[]; domain?: string }) =>
    request<WhitelistResult>(`/tenants/${tenantId}/email/whitelist`, { method: 'POST', body }),
  removeWhitelist: (tenantId: string, body: { entries: string[]; domain?: string }) =>
    request<WhitelistResult>(`/tenants/${tenantId}/email/whitelist`, { method: 'DELETE', body }),
};

/**
 * `/email/*`: the same calls for any domain named directly. Staff only, no
 * tenant mapping — this is what the Email lab uses to validate a domain
 * before, or without, assigning it to a tenant.
 */
export const emailToolsApi = {
  credentials: () => request<CredentialsCheck>('/email/credentials'),
  check: (domain: string) => request<DomainCheck>('/email/check', { query: { domain } }),
  createMailbox: (body: {
    domain: string;
    localPart: string;
    password: string;
    quotaMb?: number;
  }) => request<MailboxResult>('/email/mailboxes', { method: 'POST', body }),
  addWhitelist: (body: { domain: string; entries: string[] }) =>
    request<WhitelistResult>('/email/whitelist', { method: 'POST', body }),
  removeWhitelist: (body: { domain: string; entries: string[] }) =>
    request<WhitelistResult>('/email/whitelist', { method: 'DELETE', body }),
};

/** The tenant's domains — the mapping the hub owns. */
export function useTenantDomains(tenantId: string | null, options: QueryOptions = {}) {
  return useQuery({
    queryKey: keys.email.domains(tenantId),
    queryFn: tenantId ? () => emailApi.domains(tenantId) : skipToken,
    ...options,
  });
}

/**
 * The live DNS/MXroute answer for a domain.
 *
 * Deliberately not cached across mounts: the whole point is that it changes as
 * somebody fixes the zone, so a stale "still missing MX" would be misleading.
 */
export function useDomainCheck(
  tenantId: string | null,
  domain?: string,
  options: QueryOptions = {},
) {
  return useQuery({
    queryKey: keys.email.check(tenantId, domain ?? null),
    queryFn: tenantId ? () => emailApi.check(tenantId, domain) : skipToken,
    staleTime: 0,
    ...options,
  });
}

/** The same live answer for a domain named directly (staff). */
export function useDomainToolsCheck(domain: string | null, options: QueryOptions = {}) {
  return useQuery({
    queryKey: keys.email.toolsCheck(domain),
    queryFn: domain ? () => emailToolsApi.check(domain) : skipToken,
    staleTime: 0,
    ...options,
  });
}

/** Both provider credentials, on demand — pass `enabled: false` and `refetch()` from a button. */
export function useEmailCredentials(options: QueryOptions = {}) {
  return useQuery({
    queryKey: keys.email.credentials,
    queryFn: emailToolsApi.credentials,
    staleTime: 0,
    ...options,
  });
}

/** Every email write refreshes the check, because it is what shows the result. */
function useEmailMutation<TVars, TData>(mutationFn: (vars: TVars) => Promise<TData>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => invalidate(queryClient, keys.email.all),
  });
}

export function useCreateMailbox(tenantId: string) {
  return useEmailMutation((body: Parameters<typeof emailApi.createMailbox>[1]) =>
    emailApi.createMailbox(tenantId, body),
  );
}

/**
 * Sender allow-list, staff only.
 *
 * **Account-wide at MXroute**: an entry added for one tenant applies to every
 * domain on the account. The UI says so next to the field.
 */
export function useAddWhitelistEntries(tenantId: string) {
  return useEmailMutation((body: { entries: string[]; domain?: string }) =>
    emailApi.addWhitelist(tenantId, body),
  );
}

export function useRemoveWhitelistEntries(tenantId: string) {
  return useEmailMutation((body: { entries: string[]; domain?: string }) =>
    emailApi.removeWhitelist(tenantId, body),
  );
}

/** The direct-domain writes behind the Email lab. Same account-wide caveat for the allow-list. */
export function useToolsCreateMailbox() {
  return useEmailMutation(emailToolsApi.createMailbox);
}

export function useToolsAddWhitelistEntries() {
  return useEmailMutation(emailToolsApi.addWhitelist);
}

export function useToolsRemoveWhitelistEntries() {
  return useEmailMutation(emailToolsApi.removeWhitelist);
}
